import { useState } from 'react';
import {
  Button, IconButton, Tooltip, Chip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Typography,
} from '@mui/material';
import { Add, Edit, Delete, DeleteOutline } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { checklistTemplatesApi, type ChecklistTemplate, type ChecklistTemplateItem, type CreateChecklistInput } from '../../../api/evcpdAmc.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const APPLIES = ['ac_charger', 'dc_charger', 'panel', 'earthing', 'generic', 'custom'];

export function ChecklistTemplatesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ChecklistTemplate | null>(null);
  const [form, setForm] = useState<CreateChecklistInput>({ code: '', name: '', appliesTo: 'generic', items: [] });
  const [deleting, setDeleting] = useState<ChecklistTemplate | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['checklist-templates', page, limit], queryFn: () => checklistTemplatesApi.list({ page, limit }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['checklist-templates'] });

  const openCreate = () => { setEditing(null); setForm({ code: '', name: '', appliesTo: 'generic', items: [{ title: '', isMandatory: true }] }); setErr(null); setOpen(true); };
  const openEdit = async (t: ChecklistTemplate) => {
    const full = await checklistTemplatesApi.get(t.id);
    setEditing(full);
    setForm({ code: full.code, name: full.name, appliesTo: full.appliesTo, status: full.status, items: (full.items ?? []).map((i) => ({ title: i.title, expected: i.expected ?? undefined, isMandatory: i.isMandatory })) });
    setErr(null); setOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: () => (editing ? checklistTemplatesApi.update(editing.id, { name: form.name, appliesTo: form.appliesTo, items: form.items }) : checklistTemplatesApi.create(form)),
    onSuccess: () => { setOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => checklistTemplatesApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const setItem = (idx: number, patch: Partial<ChecklistTemplateItem>) => setForm((f) => ({ ...f, items: (f.items ?? []).map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items ?? []), { title: '', isMandatory: false }] }));
  const removeItem = (idx: number) => setForm((f) => ({ ...f, items: (f.items ?? []).filter((_, i) => i !== idx) }));

  const columns: Column<ChecklistTemplate>[] = [
    { key: 'code', label: 'Code', width: 180 },
    { key: 'name', label: 'Name' },
    { key: 'appliesTo', label: 'Applies To', render: (r) => r.appliesTo.replace(/_/g, ' ') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader title="PM Checklist Templates" subtitle="Configurable preventive-maintenance checklists (AC/DC charger, panel, earthing, custom)."
        action={hasPermission('evcpd_checklist.create') && <Button startIcon={<Add />} variant="contained" onClick={openCreate}>New Template</Button>} />
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('evcpd_checklist.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('evcpd_checklist.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'New Checklist Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {err && <Alert severity="error">{err}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} disabled={Boolean(editing)} fullWidth />
              <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
              <TextField select label="Applies To" value={form.appliesTo ?? 'generic'} onChange={(e) => setForm((f) => ({ ...f, appliesTo: e.target.value }))} sx={{ minWidth: 160 }}>
                {APPLIES.map((a) => <MenuItem key={a} value={a}>{a.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Checklist items</Typography>
              <Button size="small" startIcon={<Add />} onClick={addItem}>Add Item</Button>
            </Stack>
            <Table size="small">
              <TableHead><TableRow><TableCell>Title</TableCell><TableCell>Expected</TableCell><TableCell align="center">Mandatory</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {(form.items ?? []).map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell><TextField size="small" value={it.title} onChange={(e) => setItem(idx, { title: e.target.value })} fullWidth /></TableCell>
                    <TableCell><TextField size="small" value={it.expected ?? ''} onChange={(e) => setItem(idx, { expected: e.target.value })} fullWidth /></TableCell>
                    <TableCell align="center"><Checkbox checked={Boolean(it.isMandatory)} onChange={(e) => setItem(idx, { isMandatory: e.target.checked })} /></TableCell>
                    <TableCell align="right"><IconButton size="small" color="error" onClick={() => removeItem(idx)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.code || !form.name || !(form.items ?? []).every((i) => i.title)} onClick={() => saveMut.mutate()}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete template" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
